import { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import { Checkbox } from "../ui/checkbox";
import {
  Heart,
  AlertTriangle,
  Plus,
  Trash2,
  X,
  FileText,
} from "lucide-react";
import { listStudents, updateStudentRegistry } from "../../lib/api";
import { toast } from "sonner";

const COMMON_ALLERGIES = [
  "Arachides",
  "Fruits à coque",
  "Lait",
  "Œufs",
  "Gluten",
  "Poisson",
  "Crustacés",
  "Soja",
  "Sésame",
  "Céleri",
  "Moutarde",
  "Lupin",
];

export function HealthModule({ onClose }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStudent, setEditingStudent] = useState(null);
  const [filter, setFilter] = useState("all"); // all, pai, allergies

  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const response = await listStudents();
        setStudents(response.data || []);
      } catch (err) {
        console.error("Erreur chargement élèves:", err);
        toast.error("Erreur lors du chargement des élèves");
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, []);

  const handleUpdateHealth = async (studentId, healthInfo) => {
    try {
      await updateStudentRegistry(studentId, { health_info: healthInfo });
      toast.success("Informations de santé mises à jour");
      setEditingStudent(null);
      // Refresh students
      const response = await listStudents();
      setStudents(response.data || []);
    } catch (err) {
      console.error("Erreur mise à jour:", err);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Stats
  const paiCount = students.filter(s => s.health_info?.pai).length;
  const allergiesCount = students.filter(s => s.health_info?.allergies?.length > 0).length;

  // Filter students
  const filteredStudents = students.filter(s => {
    if (filter === "pai") return s.health_info?.pai;
    if (filter === "allergies") return s.health_info?.allergies?.length > 0;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold text-slate-800">Santé et allergies</h2>
        <p className="text-sm text-slate-500">Informations médicales des élèves</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <Card 
          className={`cursor-pointer transition-all ${filter === "all" ? "ring-2 ring-slate-400" : ""}`}
          onClick={() => setFilter("all")}
        >
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-slate-700">{students.length}</p>
            <p className="text-xs text-slate-500">Total élèves</p>
          </CardContent>
        </Card>
        <Card 
          className={`bg-rose-50 border-rose-200 cursor-pointer transition-all ${filter === "pai" ? "ring-2 ring-rose-400" : ""}`}
          onClick={() => setFilter("pai")}
        >
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-rose-700">{paiCount}</p>
            <p className="text-xs text-rose-600">PAI</p>
          </CardContent>
        </Card>
        <Card 
          className={`bg-amber-50 border-amber-200 cursor-pointer transition-all ${filter === "allergies" ? "ring-2 ring-amber-400" : ""}`}
          onClick={() => setFilter("allergies")}
        >
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{allergiesCount}</p>
            <p className="text-xs text-amber-600">Allergies</p>
          </CardContent>
        </Card>
      </div>

      {/* Liste des élèves */}
      <ScrollArea className="h-[350px] sm:h-[400px]">
        <div className="space-y-2">
          {filteredStudents.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Heart className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>{filter === "all" ? "Aucun élève enregistré" : "Aucun élève avec ces critères"}</p>
            </div>
          ) : (
            filteredStudents.map((student) => {
              const healthInfo = student.health_info || {};
              const isEditingThis = editingStudent === student.id;
              const hasAlerts = healthInfo.pai || healthInfo.allergies?.length > 0;

              return (
                <Card
                  key={student.id}
                  className={`transition-all ${hasAlerts ? "border-rose-300 bg-rose-50/30" : ""}`}
                >
                  <CardContent className="p-3">
                    {isEditingThis ? (
                      <EditHealthForm
                        student={student}
                        onSave={(info) => handleUpdateHealth(student.id, info)}
                        onCancel={() => setEditingStudent(null)}
                      />
                    ) : (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasAlerts ? "bg-rose-100" : "bg-slate-100"}`}>
                            <span className={`font-medium ${hasAlerts ? "text-rose-700" : "text-slate-700"}`}>
                              {student.student_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800">{student.student_name}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {healthInfo.pai && (
                                <Badge className="text-xs bg-rose-100 text-rose-700 border-rose-300">
                                  <FileText className="w-3 h-3 mr-1" />
                                  PAI
                                </Badge>
                              )}
                              {healthInfo.allergies?.map((allergy, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  {allergy}
                                </Badge>
                              ))}
                              {!hasAlerts && (
                                <span className="text-xs text-slate-400">Aucune information médicale</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="self-end sm:self-auto"
                          onClick={() => setEditingStudent(student.id)}
                        >
                          Modifier
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function EditHealthForm({ student, onSave, onCancel }) {
  const [info, setInfo] = useState({
    pai: student.health_info?.pai || false,
    pai_details: student.health_info?.pai_details || "",
    allergies: student.health_info?.allergies || [],
    medical_notes: student.health_info?.medical_notes || "",
  });
  const [newAllergy, setNewAllergy] = useState("");

  const addAllergy = (allergy) => {
    if (allergy && !info.allergies.includes(allergy)) {
      setInfo(prev => ({
        ...prev,
        allergies: [...prev.allergies, allergy]
      }));
      setNewAllergy("");
    }
  };

  const removeAllergy = (index) => {
    setInfo(prev => ({
      ...prev,
      allergies: prev.allergies.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-4">
      {/* PAI */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`pai-${student.id}`}
            checked={info.pai}
            onCheckedChange={(checked) => setInfo(prev => ({ ...prev, pai: checked }))}
          />
          <label htmlFor={`pai-${student.id}`} className="text-sm font-medium">
            Projet d'Accueil Individualisé (PAI)
          </label>
        </div>
        {info.pai && (
          <Textarea
            value={info.pai_details}
            onChange={(e) => setInfo(prev => ({ ...prev, pai_details: e.target.value }))}
            placeholder="Détails du PAI..."
            className="text-sm"
            rows={2}
          />
        )}
      </div>

      {/* Allergies */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Allergies</label>
        <div className="flex flex-wrap gap-1 mb-2">
          {info.allergies.map((allergy, i) => (
            <Badge key={i} variant="secondary" className="gap-1">
              {allergy}
              <button onClick={() => removeAllergy(i)} className="ml-1 hover:text-red-600">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newAllergy}
            onChange={(e) => setNewAllergy(e.target.value)}
            placeholder="Ajouter une allergie..."
            className="text-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addAllergy(newAllergy);
              }
            }}
          />
          <Button size="sm" variant="outline" onClick={() => addAllergy(newAllergy)}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {COMMON_ALLERGIES.filter(a => !info.allergies.includes(a)).slice(0, 6).map((allergy) => (
            <Badge
              key={allergy}
              variant="outline"
              className="cursor-pointer text-xs hover:bg-amber-50"
              onClick={() => addAllergy(allergy)}
            >
              + {allergy}
            </Badge>
          ))}
        </div>
      </div>

      {/* Notes médicales */}
      <div>
        <label className="text-sm font-medium mb-1 block">Notes médicales</label>
        <Textarea
          value={info.medical_notes}
          onChange={(e) => setInfo(prev => ({ ...prev, medical_notes: e.target.value }))}
          placeholder="Autres informations médicales..."
          className="text-sm"
          rows={2}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Annuler
        </Button>
        <Button size="sm" onClick={() => onSave(info)}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

export default HealthModule;
